import { useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { FileText, ArrowUpRight, ExternalLink } from "lucide-react";
import { caseStudies, drivePdfEmbed, driveOpenUrl, driveThumb } from "../data/videos";

function StudyCard({ study, index, onOpen }) {
  const [imgOk, setImgOk] = useState(true);
  const poster = driveThumb(study.driveId);

  return (
    <motion.button
      data-testid={`case-study-card-${study.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      onClick={() => onOpen(study)}
      className="group block w-full text-left"
    >
      <div className="relative overflow-hidden rounded-xl border border-white/10 bg-[#0d0d0f] transition-colors duration-500 group-hover:border-[#CBFF3A]/50">
        {/* Document cover — compact catalogue aspect */}
        <div className="relative aspect-[4/5]">
          {imgOk ? (
            <img
              src={poster}
              alt={study.title}
              loading="lazy"
              referrerPolicy="no-referrer"
              onError={() => setImgOk(false)}
              className="absolute inset-0 h-full w-full object-cover opacity-80 transition-all duration-700 group-hover:scale-105 group-hover:opacity-95"
            />
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1f] via-[#0f0f12] to-black" />
              <div
                className="absolute inset-0 opacity-[0.10] mix-blend-overlay"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 30% 20%, rgba(203,255,58,0.6), transparent 55%), radial-gradient(circle at 80% 80%, rgba(203,255,58,0.35), transparent 60%)",
                }}
              />
              <div
                aria-hidden
                className="absolute inset-0 opacity-[0.08]"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0 1px, transparent 1px 3px)",
                }}
              />
              <div className="pointer-events-none absolute right-0 top-0 h-0 w-0 border-b-[48px] border-l-[48px] border-b-transparent border-l-[#CBFF3A]/10" />
              <div className="absolute inset-0 flex items-center justify-center opacity-30 transition-opacity duration-500 group-hover:opacity-45">
                <FileText className="h-16 w-16 text-[#CBFF3A]/70" strokeWidth={1} />
              </div>
            </>
          )}
          <span className="absolute left-3 top-3 h-3 w-3 border-l border-t border-[#CBFF3A]/70" />
          <span className="absolute right-3 top-3 h-3 w-3 border-r border-t border-[#CBFF3A]/70" />
          <span className="absolute bottom-3 left-3 h-3 w-3 border-b border-l border-[#CBFF3A]/70" />
          <span className="absolute bottom-3 right-3 h-3 w-3 border-b border-r border-[#CBFF3A]/70" />
          {imgOk && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-black/15" />
          )}
          <span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full border border-[#CBFF3A]/40 bg-black/55 px-2.5 py-0.5 font-hud text-[9px] uppercase tracking-[0.2em] text-[#CBFF3A] backdrop-blur-sm">
            <FileText className="h-2.5 w-2.5" /> {study.pages || "PDF"}
          </span>
        </div>

        {/* Caption */}
        <div className="flex flex-col gap-3 p-4 md:p-5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-hud text-[9px] uppercase tracking-[0.3em] text-[#CBFF3A]/80">
              {String(index + 1).padStart(2, "0")} · Study
            </span>
            <span className="font-hud text-[9px] uppercase tracking-[0.2em] text-white/40">
              {study.year}
            </span>
          </div>
          <h3 className="font-display text-lg font-600 leading-tight tracking-tight text-white">
            {study.title}
          </h3>
          <p className="font-body text-xs text-white/55 line-clamp-2">
            {study.client}
          </p>
          <div className="mt-1 flex items-center justify-between border-t border-white/10 pt-3">
            <span className="flex items-center gap-2 font-hud text-[9px] uppercase tracking-[0.2em] text-white/50 transition-colors duration-500 group-hover:text-[#CBFF3A]">
              <span className="inline-block h-[1px] w-5 bg-current" />
              Open PDF
            </span>
            <ArrowUpRight
              className="h-4 w-4 text-white/40 transition-all duration-500 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#CBFF3A]"
              strokeWidth={1.5}
            />
          </div>
        </div>
      </div>
    </motion.button>
  );
}

function PdfViewer({ study, onClose }) {
  if (!study) return null;
  return (
    <Dialog open={!!study} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="case-study-modal"
        className="z-[95] max-w-5xl border-white/15 bg-[#060606] p-0"
      >
        <DialogTitle className="sr-only">{study.title}</DialogTitle>
        <DialogDescription className="sr-only">
          {study.client} · Case study PDF
        </DialogDescription>

        <div className="h-[80vh] w-full overflow-hidden bg-black">
          <iframe
            title={study.title}
            src={drivePdfEmbed(study.driveId)}
            className="h-full w-full"
            allow="autoplay; fullscreen"
            allowFullScreen
          />
        </div>

        <div className="flex items-center justify-between gap-4 px-6 py-4">
          <div>
            <div className="font-hud text-[10px] uppercase tracking-[0.2em] text-[#CBFF3A]">
              {study.client}
            </div>
            <h3 className="mt-1 font-display text-lg font-600 tracking-tight text-white">
              {study.title}
            </h3>
          </div>
          <a
            href={driveOpenUrl(study.driveId)}
            target="_blank"
            rel="noreferrer"
            data-testid="case-study-open-external"
            className="flex shrink-0 items-center gap-2 rounded-full border border-white/20 px-4 py-2 font-hud text-[10px] uppercase tracking-widest text-white/70 transition-colors hover:border-[#CBFF3A] hover:text-[#CBFF3A]"
          >
            Open in Drive <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CaseStudies() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="case-studies"
      data-testid="case-studies-section"
      className="relative z-10 mx-auto max-w-[1400px] px-6 py-28 md:px-16 md:py-32"
    >
      <div className="mb-14 flex flex-col gap-3">
        <span className="font-hud text-[11px] uppercase tracking-[0.3em] text-[#CBFF3A]/80">
          / Case Studies
        </span>
        <h2 className="font-display text-4xl font-600 tracking-tight text-[#ECEAE3] md:text-5xl">
          Deep-Dive <em className="not-italic text-[#CBFF3A]">Documents</em>
        </h2>
        <p className="max-w-xl font-body text-white/55">
          Full-length breakdowns of selected projects — brief, approach, credits,
          numbers. Click any card to read the PDF right here.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {caseStudies.map((cs, i) => (
          <StudyCard key={cs.id} study={cs} index={i} onOpen={setActive} />
        ))}
      </div>

      <PdfViewer study={active} onClose={() => setActive(null)} />
    </section>
  );
}
